import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, ChevronLeft, ChevronRight, Sparkles, Sword, Shield, Shirt, ArrowDownWideNarrow, ArrowUpNarrowWide, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { btn, Loader, Empty, PageHeader } from "@/components/Page";
import Modal from "@/components/Modal";
import { SKILL_META, RARITY, RARITY_ORDER } from "@/lib/game";
import { DurabilityBar } from "@/components/GladiatorCard";
import { HelmetGlyph } from "@/components/HelmetGlyph";

const ITEMS_PER_PAGE = 12;

// Consumable image mapping
const CONSUMABLE_IMAGES = {
  recovery_ether: "/recovery-ether.png",
  campaign_stamina_potion: "/campaign-stamina-v2.png",
  instant_scout: "/instant-scout.png",
  experienced_medicus: "/medicus.png",
  love_potion: "/love-potion.png",
  beast_food_strength: "/beast-food-v3.png",
  beast_food_stamina: "/beast-food-v3.png",
  beast_food_speed: "/beast-food-v3.png",
  beast_food_technique: "/beast-food-v3.png",
  beast_food_precision: "/beast-food-v3.png",
  beast_food_defense: "/beast-food-v3.png",
  beast_supplement: "/beast-supplement.png"
};

const HelmetSlotIcon = ({ size = 12, className = "" }) => <HelmetGlyph size={size} className={className} />;

function ItemMeta({ item }) {
  const mods = item.mods || item.base_mods || {};
  return (
    <>
      <div className="flex gap-2 mt-1.5 flex-wrap">
        {Object.entries(mods).map(([k, v]) => (
          <span key={k} className="text-[10px] text-emerald-400">+{v} {SKILL_META[k]?.label || k}</span>
        ))}
      </div>
      {(item.affixes || []).length > 0 && (
        <div className="flex gap-1.5 mt-1.5 flex-wrap">
          {item.affixes.map((af, i) => (
            <span key={i}
              className="text-[10px] text-violet-300 border border-violet-800/40 bg-violet-950/20 px-1.5 py-0.5 rounded-sm flex items-center gap-1">
              <Sparkles size={9} /> {af.display}
            </span>
          ))}
        </div>
      )}
      <DurabilityBar value={item.durability} />
    </>
  );
}

export default function AllianceArmoryPage() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [alliance, setAlliance] = useState(null);
  const [armoryData, setArmoryData] = useState(null);
  const [activeTab, setActiveTab] = useState("blacksmith");
  const [purchaseSubTab, setPurchaseSubTab] = useState("consumables");
  const [inventoryTab, setInventoryTab] = useState("gear");
  const [assignedPage, setAssignedPage] = useState(1);
  const [unassignedPage, setUnassignedPage] = useState(1);
  const [logModal, setLogModal] = useState(false);
  const [transactionLog, setTransactionLog] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null);
  const [assignData, setAssignData] = useState(null);
  const [helpModal, setHelpModal] = useState(false);
  const [gearSlotFilter, setGearSlotFilter] = useState("all");
  const [gearRaritySort, setGearRaritySort] = useState("desc");

  useEffect(() => {
    loadAlliance();
  }, []);

  async function loadAlliance() {
    try {
      const res = await api.get("/alliance");
      if (!res.data.alliance) {
        toast.error("You must be in an alliance to access the armory");
        navigate("/alliance");
        return;
      }
      setAlliance(res.data.alliance);
      loadArmory(res.data.alliance.id);
    } catch (err) {
      toast.error(apiErr(err));
      navigate("/alliance");
    }
  }

  async function loadArmory(allianceId) {
    try {
      const res = await api.get(`/alliances/${allianceId}/armory`);
      setArmoryData(res.data);
      if (!res.data.can_manage) setActiveTab("armory");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  }

  async function unlockTier(tier) {
    setConfirmModal({ type: 'tier', tier, cost: armoryData.catalog.tier_unlocks[tier].cost });
  }

  async function confirmUnlockTier() {
    const { tier } = confirmModal;
    setConfirmModal(null);
    setBusy(true);
    try {
      await api.post(`/alliances/${alliance.id}/armory/unlock-tier`, { tier });
      toast.success(`Tier ${tier} unlocked!`);
      loadArmory(alliance.id);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  }

  async function purchaseGear(rarity, slot, cls) {
    setConfirmModal({ type: 'gear', rarity, slot, cls, cost: armoryData.catalog.gear_costs[rarity].cost });
  }

  async function confirmPurchaseGear() {
    const { rarity, slot, cls } = confirmModal;
    setConfirmModal(null);
    setBusy(true);
    try {
      const res = await api.post(`/alliances/${alliance.id}/armory/purchase-gear`, {
        rarity,
        slot,
        cls
      });
      toast.success(`Purchased ${res.data.gear.name}`);
      loadArmory(alliance.id);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  }

  async function assignItem(armoryItemId, targetUserId, quantity = 1) {
    const item = [
      ...(armoryData.inventory.unassigned_gear || []),
      ...(armoryData.inventory.unassigned_consumables || [])
    ].find(i => i.id === armoryItemId);

    if (!item) {
      toast.error('Item not found');
      return;
    }

    const member = alliance?.members?.find(m => m.user_id === targetUserId);
    const memberName = member?.name || targetUserId;

    const itemName = item.item_type === 'gear'
      ? item.gear_details?.name
      : item.consumable_type;

    const assignInfo = { armoryItemId, targetUserId, quantity, itemName, memberName, gearDetails: item.gear_details || null };
    setAssignData(assignInfo);
    setConfirmModal({ type: 'assign' });
  }

  async function confirmAssignItem() {
    const { armoryItemId, targetUserId, quantity } = assignData;
    setConfirmModal(null);
    setAssignData(null);
    setBusy(true);
    try {
      await api.post(`/alliances/${alliance.id}/armory/assign`, {
        armory_item_id: armoryItemId,
        target_user_id: targetUserId,
        quantity
      });
      toast.success('Item assigned!');
      loadArmory(alliance.id);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  }

  async function purchaseConsumable(itemId, quantity) {
    setBusy(true);
    try {
      await api.post(`/alliances/${alliance.id}/armory/purchase-consumable`, {
        consumable_id: itemId,
        quantity
      });
      toast.success('Consumable purchased!');
      loadArmory(alliance.id);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  }

  async function loadLog() {
    try {
      const res = await api.get(`/alliances/${alliance.id}/armory/log`);
      setTransactionLog(res.data.log || []);
      setLogModal(true);
    } catch (err) {
      toast.error(apiErr(err));
    }
  }

  async function returnGear(armoryItemId) {
    setConfirmModal({ type: 'return', armoryItemId });
  }

  async function confirmReturnGear() {
    const { armoryItemId } = confirmModal;
    setConfirmModal(null);
    setBusy(true);
    try {
      const res = await api.post(`/alliances/${alliance.id}/armory/return-gear`, {
        armory_item_id: armoryItemId
      });
      toast.success(`Returned ${res.data.item_name} to the armory`);
      loadArmory(alliance.id);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Loader label="Loading armory..." />;
  if (!armoryData) return <Empty msg="Armory data unavailable" />;

  const { points, armory_tier, can_manage, catalog, inventory } = armoryData;
  const nextTier = armory_tier < 3 ? armory_tier + 1 : null;

  const unassignedGear = inventory?.unassigned_gear || [];
  const unassignedConsumables = inventory?.unassigned_consumables || [];
  const assignedGear = (inventory?.assigned || []).filter(i => i.item_type === 'gear');
  const assignedConsumables = (inventory?.assigned || []).filter(i => i.item_type === 'consumable');

  // Sort & filter gear helper
  function sortAndFilterGear(gearList) {
    const filtered = gearSlotFilter === "all"
      ? gearList
      : gearList.filter(i => (i.gear_details?.kind || i.gear_details?.slot) === gearSlotFilter);
    return [...filtered].sort((a, b) => {
      const ra = RARITY_ORDER.indexOf(a.gear_details?.rarity || "common");
      const rb = RARITY_ORDER.indexOf(b.gear_details?.rarity || "common");
      if (ra !== rb) return gearRaritySort === "desc" ? rb - ra : ra - rb;
      const ka = a.gear_details?.kind || "", kb = b.gear_details?.kind || "";
      const kindOrder = { weapon: 0, offhand: 1, armor: 2, helmet: 3 };
      if (ka !== kb) return (kindOrder[ka] ?? 99) - (kindOrder[kb] ?? 99);
      return (a.gear_details?.name || "").localeCompare(b.gear_details?.name || "");
    });
  }

  const filteredUnassignedGear = sortAndFilterGear(unassignedGear);
  const filteredAssignedGear = sortAndFilterGear(assignedGear);

  const assignedItems = inventoryTab === 'gear' ? filteredAssignedGear : assignedConsumables;
  const unassignedItems = inventoryTab === 'gear' ? filteredUnassignedGear : unassignedConsumables;

  const totalUnassignedPages = Math.max(1, Math.ceil(unassignedItems.length / ITEMS_PER_PAGE));
  const unassignedPageSafe = Math.min(unassignedPage, totalUnassignedPages);
  const paginatedUnassigned = unassignedItems.slice(
    (unassignedPageSafe - 1) * ITEMS_PER_PAGE,
    unassignedPageSafe * ITEMS_PER_PAGE
  );

  const totalAssignedPages = Math.max(1, Math.ceil(assignedItems.length / ITEMS_PER_PAGE));
  const assignedPageSafe = Math.min(assignedPage, totalAssignedPages);
  const paginatedAssigned = assignedItems.slice(
    (assignedPageSafe - 1) * ITEMS_PER_PAGE,
    assignedPageSafe * ITEMS_PER_PAGE
  );

  return (
    <div className="p-4">
      <button onClick={() => navigate('/alliance')} className="flex items-center gap-1 text-zinc-500 hover:text-amber-400 text-sm mb-4 transition-colors" data-testid="back-to-alliance">
        <ArrowLeft size={15} /> Back to Alliance
      </button>

      <PageHeader
        title="Alliance Armory"
        subtitle="Equip your alliance with legendary gear and rare consumables"
      />

      {/* Header Stats Banner — matches Equipment panel pattern */}
      <div className="bg-zinc-950 border border-amber-900/30 p-5 mb-6" data-testid="armory-stats-banner">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-8">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Alliance Points</p>
              <p className="font-cinzel text-3xl text-amber-300 tabular-nums" data-testid="alliance-points">{points.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Armory Tier</p>
              <p className={`font-cinzel text-3xl tabular-nums ${armory_tier >= 3 ? 'text-amber-400' : armory_tier >= 2 ? 'text-violet-400' : 'text-sky-400'}`} data-testid="armory-tier">{armory_tier}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setHelpModal(true)} data-testid="armory-help-btn"
              className="text-[11px] uppercase tracking-widest px-3 py-1.5 border border-zinc-800 text-zinc-400 hover:border-amber-700/50 hover:text-amber-300 transition-colors flex items-center gap-1.5">
              <HelpCircle size={13} /> How does this work?
            </button>
            <button onClick={loadLog} data-testid="transaction-log-btn"
              className="text-[11px] uppercase tracking-widest px-3 py-1.5 border border-zinc-800 text-zinc-400 hover:border-amber-700/50 hover:text-amber-300 transition-colors">
              Transaction Log
            </button>
          </div>
        </div>
      </div>

      {/* Tier Unlock — matches Equipment upgrade panels */}
      {nextTier && catalog?.tier_unlocks?.[nextTier] && (
        <div className="bg-zinc-950 border border-amber-900/30 p-5 mb-6" data-testid="tier-unlock-panel">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="font-cinzel text-lg text-amber-300 uppercase tracking-wide">{catalog.tier_unlocks[nextTier].name}</p>
              <p className="text-sm text-zinc-500 mt-1">{catalog.tier_unlocks[nextTier].unlocks}</p>
            </div>
            {can_manage && (
              <button
                onClick={() => unlockTier(nextTier)}
                disabled={busy || points < catalog.tier_unlocks[nextTier].cost}
                data-testid="unlock-tier-btn"
                className={`text-[11px] uppercase tracking-widest px-4 py-2 border transition-colors ${
                  points >= catalog.tier_unlocks[nextTier].cost
                    ? "border-amber-600 text-amber-300 hover:bg-amber-900/30"
                    : "border-zinc-800 text-zinc-600 cursor-not-allowed"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : `Unlock · ${catalog.tier_unlocks[nextTier].cost} pts`}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main Tabs — matches Equipment tab pattern exactly */}
      <div className="flex gap-2 mb-6 border-b border-zinc-800">
        {[
          { id: "blacksmith", label: "Purchase" },
          { id: "armory", label: "Inventory" }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`tab-${tab.id}`}
            className={`px-4 py-2 text-sm font-cinzel uppercase tracking-wide -mb-px border-b-2 transition-colors ${
              activeTab === tab.id
                ? "border-amber-500 text-amber-300"
                : "border-transparent text-zinc-500 hover:text-zinc-300"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Purchase Tab */}
      {activeTab === "blacksmith" && (
        <div data-testid="purchase-tab">
          {/* Sub-tabs — matches Equipment filter button pattern */}
          <div className="flex items-center border border-zinc-800 rounded-sm overflow-hidden mb-6 w-fit">
            {["consumables", "gear"].map(sub => (
              <button
                key={sub}
                onClick={() => setPurchaseSubTab(sub)}
                data-testid={`purchase-subtab-${sub}`}
                className={`px-4 py-1.5 text-[11px] uppercase tracking-widest transition-colors capitalize ${
                  purchaseSubTab === sub
                    ? "bg-amber-900/30 text-amber-300"
                    : "text-zinc-500 hover:text-zinc-300"
                }`}
              >
                {sub}
              </button>
            ))}
          </div>

          {/* Consumables */}
          {purchaseSubTab === "consumables" && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {catalog.consumables?.map(item => (
                <ConsumableCard
                  key={item.id}
                  item={item}
                  onPurchase={purchaseConsumable}
                  canManage={can_manage}
                  busy={busy}
                  points={points}
                />
              ))}
            </div>
          )}

          {/* Gear */}
          {purchaseSubTab === "gear" && (
            <div className="space-y-4">
              {Object.entries(catalog.gear_costs || {}).map(([rarity, data]) => (
                <GearSection
                  key={rarity}
                  rarity={rarity}
                  cost={data.cost}
                  onPurchase={purchaseGear}
                  canManage={can_manage}
                  busy={busy}
                  points={points}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Inventory Tab */}
      {activeTab === "armory" && (
        <div data-testid="inventory-tab">
          {/* Inventory Sub-tabs */}
          <div className="flex items-center border border-zinc-800 rounded-sm overflow-hidden mb-6 w-fit">
            {["gear", "consumables"].map(sub => (
              <button
                key={sub}
                onClick={() => {
                  setInventoryTab(sub);
                  setAssignedPage(1);
                  setUnassignedPage(1);
                }}
                data-testid={`inventory-subtab-${sub}`}
                className={`px-4 py-1.5 text-[11px] uppercase tracking-widest transition-colors capitalize ${
                  inventoryTab === sub
                    ? "bg-amber-900/30 text-amber-300"
                    : "text-zinc-500 hover:text-zinc-300"
                }`}
              >
                {sub}
              </button>
            ))}
          </div>

          {/* Sort/Filter controls — only for gear tab, matches Equipment armory pattern */}
          {inventoryTab === 'gear' && (unassignedGear.length > 0 || assignedGear.length > 0) && (
            <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="gear-sort-controls">
              <div className="flex items-center border border-zinc-800 rounded-sm overflow-hidden">
                {[["all", "All", Sparkles], ["weapon", "Weapons", Sword], ["offhand", "Off-hand", Shield], ["armor", "Armor", Shirt], ["helmet", "Helmets", HelmetSlotIcon]].map(([val, label, Icon]) => (
                  <button key={val} data-testid={`gear-filter-${val}`}
                    onClick={() => { setGearSlotFilter(val); setAssignedPage(1); setUnassignedPage(1); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-widest transition-colors ${
                      gearSlotFilter === val ? "bg-amber-900/30 text-amber-300" : "text-zinc-500 hover:text-zinc-300"
                    }`}>
                    <Icon size={12} /> {label}
                  </button>
                ))}
              </div>
              <button data-testid="gear-sort-rarity"
                onClick={() => { setGearRaritySort(s => s === "desc" ? "asc" : "desc"); setAssignedPage(1); setUnassignedPage(1); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-widest border border-zinc-800 text-zinc-400 hover:border-amber-700/60 hover:text-amber-300 transition-colors rounded-sm">
                {gearRaritySort === "desc" ? <ArrowDownWideNarrow size={13} /> : <ArrowUpNarrowWide size={13} />}
                Rarity {gearRaritySort === "desc" ? "High→Low" : "Low→High"}
              </button>
              <span className="text-[11px] text-zinc-600 ml-auto tabular-nums">
                {filteredUnassignedGear.length + filteredAssignedGear.length} item{(filteredUnassignedGear.length + filteredAssignedGear.length) === 1 ? "" : "s"}
              </span>
            </div>
          )}

          {/* Unassigned Section */}
          <section className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm flex items-center gap-2">
                <Sparkles size={15} /> Unassigned {inventoryTab === 'gear' ? 'Gear' : 'Consumables'}
              </h3>
              {unassignedItems.length > ITEMS_PER_PAGE && (
                <div className="flex items-center gap-2">
                  <button onClick={() => setUnassignedPage(p => Math.max(1, p - 1))} disabled={unassignedPageSafe === 1}
                    data-testid="unassigned-prev"
                    className="flex items-center gap-1 px-3 py-1.5 border border-zinc-800 text-zinc-300 hover:border-amber-600 hover:text-amber-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm">
                    <ChevronLeft size={15} /> Prev
                  </button>
                  <span className="text-[11px] uppercase tracking-widest text-zinc-500 tabular-nums">
                    {unassignedPageSafe} / {totalUnassignedPages}
                  </span>
                  <button onClick={() => setUnassignedPage(p => Math.min(totalUnassignedPages, p + 1))} disabled={unassignedPageSafe === totalUnassignedPages}
                    data-testid="unassigned-next"
                    className="flex items-center gap-1 px-3 py-1.5 border border-zinc-800 text-zinc-300 hover:border-amber-600 hover:text-amber-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm">
                    Next <ChevronRight size={15} />
                  </button>
                </div>
              )}
            </div>
            {inventoryTab === 'gear' ? (
              paginatedUnassigned.length === 0 ? (
                <div className="border border-dashed border-amber-900/40 bg-zinc-950/50 p-8 text-center text-zinc-500">
                  {gearSlotFilter === "all" ? "No unassigned gear in armory" : `No unassigned ${gearSlotFilter} gear`}
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {paginatedUnassigned.map(item => (
                    <GearItemCard
                      key={item.id}
                      item={item}
                      members={armoryData.members}
                      onAssign={assignItem}
                      canManage={can_manage}
                    />
                  ))}
                </div>
              )
            ) : (
              paginatedUnassigned.length === 0 ? (
                <div className="border border-dashed border-amber-900/40 bg-zinc-950/50 p-8 text-center text-zinc-500">
                  No unassigned consumables in armory
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {paginatedUnassigned.map(item => (
                    <ConsumableItemCard
                      key={item.id}
                      item={item}
                      members={armoryData.members}
                      onAssign={assignItem}
                      canManage={can_manage}
                    />
                  ))}
                </div>
              )
            )}
          </section>

          {/* Assigned Section with Pagination */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm">
                Assigned {inventoryTab === 'gear' ? 'Gear' : 'Consumables'}
              </h3>
              {assignedItems.length > ITEMS_PER_PAGE && (
                <div className="flex items-center gap-2">
                  <button onClick={() => setAssignedPage(p => Math.max(1, p - 1))} disabled={assignedPageSafe === 1}
                    data-testid="assigned-prev"
                    className="flex items-center gap-1 px-3 py-1.5 border border-zinc-800 text-zinc-300 hover:border-amber-600 hover:text-amber-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm">
                    <ChevronLeft size={15} /> Prev
                  </button>
                  <span className="text-[11px] uppercase tracking-widest text-zinc-500 tabular-nums">
                    {assignedPageSafe} / {totalAssignedPages}
                  </span>
                  <button onClick={() => setAssignedPage(p => Math.min(totalAssignedPages, p + 1))} disabled={assignedPageSafe === totalAssignedPages}
                    data-testid="assigned-next"
                    className="flex items-center gap-1 px-3 py-1.5 border border-zinc-800 text-zinc-300 hover:border-amber-600 hover:text-amber-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm">
                    Next <ChevronRight size={15} />
                  </button>
                </div>
              )}
            </div>
            {paginatedAssigned.length === 0 ? (
              <div className="border border-dashed border-amber-900/40 bg-zinc-950/50 p-8 text-center text-zinc-500">
                No assigned {inventoryTab}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {paginatedAssigned.map(item => (
                  <AssignedItemCard key={item.id} item={item} currentUserId={authUser?.user_id} onReturn={returnGear} busy={busy} />
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {/* Help Modal */}
      {helpModal && (
        <Modal open={helpModal} onClose={() => setHelpModal(false)} title="How Does the Armory Work?">
          <div className="space-y-4 text-sm text-zinc-300 leading-relaxed">
            <div>
              <h4 className="font-cinzel text-amber-300 uppercase tracking-wide text-xs mb-1.5">Purchasing</h4>
              <p>Alliance leaders (and magisters with armory permission) can spend <span className="text-amber-300">alliance points</span> to purchase consumable items and forge gear. Higher armory tiers unlock rarer equipment.</p>
            </div>
            <div>
              <h4 className="font-cinzel text-amber-300 uppercase tracking-wide text-xs mb-1.5">Assigning Gear</h4>
              <p>Purchased gear is stored in the shared armory pool. Leaders can assign any piece to an alliance member, who will receive an inbox notification. That member can then equip the gear on their gladiators as normal.</p>
            </div>
            <div>
              <h4 className="font-cinzel text-amber-300 uppercase tracking-wide text-xs mb-1.5">Returning Gear</h4>
              <p>Alliance gear is <span className="text-amber-300">on loan</span>, not a gift. Members can voluntarily return gear from the Armory inventory page or from their Equipment page. If a member is <span className="text-amber-300">inactive for 7+ days</span>, their assigned gear is automatically returned to the armory.</p>
            </div>
            <div>
              <h4 className="font-cinzel text-amber-300 uppercase tracking-wide text-xs mb-1.5">Restrictions</h4>
              <p>Alliance gear <span className="text-red-400">cannot be melted down</span>, sold, or listed on the auction house. It must stay within the alliance. If a gladiator retires while wearing alliance gear, the gear is returned to the member&#39;s personal armory.</p>
            </div>
            <div>
              <h4 className="font-cinzel text-amber-300 uppercase tracking-wide text-xs mb-1.5">Repairing Gear</h4>
              <p>Alliance gear is just like any other gear and needs to be repaired or it will break. Alliances do not share weapon fragments, so <span className="text-amber-300">you will need to repair items yourself</span> before returning them, or send them to a veteran member who can. If alliance gear breaks, the alliance points spent to purchase it are wasted.</p>
            </div>
            <div>
              <h4 className="font-cinzel text-amber-300 uppercase tracking-wide text-xs mb-1.5">Consumables</h4>
              <p>Consumable items (potions, beast food, etc.) are assigned directly to a member and added to their personal inventory. Unlike gear, consumables are used up and do not return to the armory.</p>
            </div>
            <div className="border-t border-zinc-800 pt-3 text-xs text-zinc-500">
              Armory activity is tracked in the Transaction Log for full transparency.
            </div>
          </div>
        </Modal>
      )}

      {/* Transaction Log Modal */}
      {logModal && (
        <Modal open={logModal} onClose={() => setLogModal(false)} title="Armory Transaction Log">
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {transactionLog.length === 0 ? (
              <p className="text-center text-zinc-500 py-8">No transactions yet</p>
            ) : (
              transactionLog.map(entry => (
                <div key={entry.id} className="border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-sm">
                  <p className="text-[10px] text-zinc-600 tabular-nums mb-1">{new Date(entry.timestamp).toLocaleString()}</p>
                  <p className="text-zinc-300">
                    <span className="text-violet-400">{entry.actor_name}</span>
                    {entry.action === "purchase" && ` purchased ${entry.item_name}`}
                    {entry.action === "assign" && ` assigned ${entry.item_name} to ${entry.target_user_name}`}
                    {entry.action === "return" && ` returned ${entry.item_name} to the armory`}
                    {entry.action === "unlock_tier" && ` unlocked ${entry.item_name}`}
                    {entry.cost && <span className="text-amber-300 ml-2 tabular-nums">(-{entry.cost} pts)</span>}
                  </p>
                </div>
              ))
            )}
          </div>
        </Modal>
      )}

      {/* Confirmation Modal */}
      {confirmModal && armoryData && (
        <Modal open={true} onClose={() => { setConfirmModal(null); setAssignData(null); }} title="Confirm Action">
          <div className="space-y-4">
            {confirmModal.type === 'tier' && (
              <>
                <p className="text-zinc-300">
                  Unlock <span className="font-semibold text-violet-400">Tier {confirmModal.tier}</span> for{" "}
                  <span className="font-semibold text-amber-300 tabular-nums">{confirmModal.cost} alliance points</span>?
                </p>
                <p className="text-sm text-zinc-500">
                  {armoryData.catalog.tier_unlocks[confirmModal.tier]?.unlocks}
                </p>
              </>
            )}
            {confirmModal.type === 'gear' && (
              <>
                <p className="text-zinc-300">
                  Forge a <span className={`font-semibold capitalize ${(RARITY[confirmModal.rarity] || {}).text}`}>
                    {confirmModal.rarity}
                  </span>
                  {confirmModal.cls && <span className="font-semibold text-amber-200"> {confirmModal.cls}</span>}
                  {" "}<span className="text-zinc-400">{confirmModal.slot}</span> for{" "}
                  <span className="font-semibold text-amber-300 tabular-nums">{confirmModal.cost} alliance points</span>?
                </p>
                <p className="text-sm text-zinc-500">
                  This gear will be added to the armory and can be assigned to any alliance member.
                </p>
              </>
            )}
            {confirmModal.type === 'assign' && assignData && (
              <>
                <p className="text-zinc-300">
                  Assign <span className="font-semibold text-amber-300">{assignData.itemName}</span>
                  {assignData.quantity > 1 && <span className="tabular-nums"> x{assignData.quantity}</span>} to{" "}
                  <span className="font-semibold text-violet-400">{assignData.memberName}</span>?
                </p>
                {assignData.gearDetails && (
                  <div className="mt-3 border border-zinc-800 bg-zinc-900/40 rounded-sm p-3">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                      {assignData.gearDetails.kind} {assignData.gearDetails.class_req ? `· ${assignData.gearDetails.class_req}` : '· any class'}
                    </div>
                    <ItemMeta item={assignData.gearDetails} />
                  </div>
                )}
              </>
            )}
            {confirmModal.type === 'return' && (
              <p className="text-zinc-300">
                Return this gear to the alliance armory? It will be unequipped from your gladiators.
              </p>
            )}
            <div className="flex gap-3 pt-2">
              <button onClick={() => { setConfirmModal(null); setAssignData(null); }}
                className={`flex-1 ${btn("ghost")}`}>
                Cancel
              </button>
              <button
                onClick={() => {
                  if (confirmModal.type === 'tier') confirmUnlockTier();
                  if (confirmModal.type === 'gear') confirmPurchaseGear();
                  if (confirmModal.type === 'assign') confirmAssignItem();
                  if (confirmModal.type === 'return') confirmReturnGear();
                }}
                disabled={busy}
                className={`flex-1 ${btn()}`}
              >
                {busy ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Confirm"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ConsumableCard({ item, onPurchase, canManage, busy, points }) {
  const [quantity, setQuantity] = useState(1);
  const imageUrl = CONSUMABLE_IMAGES[item.id] || "/recovery-ether.png";

  return (
    <div className="bg-zinc-950 border border-amber-900/30 hover:border-amber-700/50 transition-colors overflow-hidden" data-testid={`consumable-${item.id}`}>
      <div className="bg-zinc-900/40 p-6 flex items-center justify-center" style={{ minHeight: '160px' }}>
        <img src={imageUrl} alt={item.name} className="max-w-full max-h-[120px] object-contain" />
      </div>

      <div className="p-4 space-y-3">
        <div>
          <h4 className="font-cinzel text-amber-300 tracking-wide text-sm">{item.name}</h4>
          <p className="text-xs text-zinc-500 mt-1">{item.description}</p>
        </div>

        <div className="text-sm text-amber-300 font-cinzel tabular-nums">{item.cost} pts</div>

        {canManage && (
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="1"
              max="100"
              value={quantity}
              onChange={e => setQuantity(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
              className="w-16 px-2 py-1.5 bg-zinc-900 border border-zinc-800 text-center text-sm tabular-nums"
              data-testid={`consumable-qty-${item.id}`}
            />
            <button
              onClick={() => onPurchase(item.id, quantity)}
              disabled={busy || points < item.cost * quantity}
              data-testid={`purchase-consumable-${item.id}`}
              className={`flex-1 text-[11px] uppercase tracking-widest px-2.5 py-2 border transition-colors ${
                points >= item.cost * quantity
                  ? "border-amber-600 text-amber-300 hover:bg-amber-900/30"
                  : "border-zinc-800 text-zinc-600 cursor-not-allowed"
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {busy ? <Loader2 size={14} className="animate-spin mx-auto" /> : "Purchase"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const WEAPON_CLASSES = [
  { cls: "Dimachaerus", label: "Dimachaerus — Twin Blades" },
  { cls: "Retiarius", label: "Retiarius — Trident" },
  { cls: "Provocator", label: "Provocator — Gladius" },
];

const OFFHAND_CLASSES = [
  { cls: "Dimachaerus", label: "Dimachaerus — Twin Blades" },
  { cls: "Retiarius", label: "Retiarius — Net" },
  { cls: "Provocator", label: "Provocator — Shield" },
];

function GearSection({ rarity, cost, onPurchase, canManage, busy, points }) {
  const [classModal, setClassModal] = useState(null); // { slot, options }
  const meta = RARITY[rarity] || RARITY.common;
  const affordable = points >= cost;
  const forgeSlots = [
    { id: "weapon", label: "Weapon" },
    { id: "offhand", label: "Off-hand" },
    { id: "armor", label: "Armor" },
    { id: "helmet", label: "Helmet" },
  ];

  function handleSlotClick(slot) {
    if (slot === "armor" || slot === "helmet") {
      // Armor has no class — go straight to confirmation
      onPurchase(rarity, slot, null);
    } else {
      const options = slot === "weapon" ? WEAPON_CLASSES : OFFHAND_CLASSES;
      setClassModal({ slot, options });
    }
  }

  function handleClassSelect(cls) {
    const slot = classModal.slot;
    setClassModal(null);
    onPurchase(rarity, slot, cls);
  }

  return (
    <>
      <section className={`max-w-full border ${meta.border} ${meta.bg} overflow-hidden`} data-testid={`gear-section-${rarity}`}>
        <div className="p-4 sm:p-5">
          <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <h3 data-testid={`gear-section-${rarity}-title`} className={`font-cinzel text-lg uppercase tracking-wide capitalize ${meta.text}`}>
              {rarity} Equipment
                </h3>
                <span data-testid={`gear-section-${rarity}-cost`} className="font-cinzel text-xs uppercase tracking-widest text-amber-300 border border-amber-900/50 bg-zinc-950/60 px-2 py-1 tabular-nums">
                  {cost} pts
                </span>
              </div>
              <p className="text-sm text-zinc-500 mt-1.5">Alliance-branded gear for your members</p>
            </div>
            {canManage && (
              <div className="grid w-full min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 lg:w-auto lg:grid-cols-4 lg:min-w-[31rem]">
                {forgeSlots.map(slot => (
                  <button
                    key={slot.id}
                    onClick={() => handleSlotClick(slot.id)}
                    disabled={busy || !affordable}
                    data-testid={`forge-${rarity}-${slot.id}`}
                    className={`w-full min-w-0 px-3 py-3 font-cinzel text-xs uppercase tracking-wide transition-colors border ${
                      busy || !affordable
                        ? "border-zinc-800 text-zinc-600 cursor-not-allowed"
                        : `${meta.border} ${meta.text} hover:bg-amber-900/30 hover:border-amber-500`
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                  >
                    {slot.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Class Selection Modal */}
      {classModal && (
        <Modal open={true} onClose={() => setClassModal(null)} title={`Choose ${classModal.slot === "weapon" ? "Weapon" : "Off-hand"} Class`}>
          <div className="space-y-2">
            <p className="text-sm text-zinc-500 mb-3">
              Select a class to forge a <span className={`capitalize font-semibold ${meta.text}`}>{rarity}</span> {classModal.slot}.
            </p>
            {classModal.options.map(opt => (
              <button
                key={opt.cls}
                onClick={() => handleClassSelect(opt.cls)}
                data-testid={`class-select-${opt.cls.toLowerCase()}`}
                className={`w-full text-left px-4 py-3 border transition-colors ${meta.border} ${meta.bg} hover:bg-amber-900/20 hover:border-amber-500`}
              >
                <span className={`font-cinzel uppercase tracking-wide ${meta.text}`}>{opt.label}</span>
              </button>
            ))}
            <button onClick={() => setClassModal(null)} className={`w-full mt-2 ${btn("ghost")}`}>Cancel</button>
          </div>
        </Modal>
      )}
    </>
  );
}

function GearItemCard({ item, members, onAssign, canManage }) {
  const [selectedMember, setSelectedMember] = useState("");

  const gear = item.gear_details || {};
  const rarity = gear.rarity || "common";
  const meta = RARITY[rarity] || RARITY.common;

  return (
    <div className={`border ${meta.border} ${meta.bg} p-4`} data-testid={`gear-item-${item.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className={`font-cinzel ${meta.text} tracking-wide`}>{gear.name || 'Unknown Gear'}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
            {gear.kind} {gear.class_req ? `· ${gear.class_req}` : '· any class'}
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border rounded-sm ${meta.text} ${meta.border} ${meta.bg}`}>
          {meta.label}
        </span>
      </div>

      <ItemMeta item={gear} />

      {canManage && (
        <div className="space-y-2 mt-3 pt-3 border-t border-zinc-800">
          <select
            value={selectedMember}
            onChange={e => setSelectedMember(e.target.value)}
            className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 text-sm"
            data-testid={`assign-select-${item.id}`}
          >
            <option value="">Select member...</option>
            {members?.map(m => (
              <option key={m.user_id} value={m.user_id}>{m.name}</option>
            ))}
          </select>
          <button
            onClick={() => onAssign(item.id, selectedMember, 1)}
            disabled={!selectedMember}
            data-testid={`assign-btn-${item.id}`}
            className={`w-full text-[11px] uppercase tracking-widest px-2.5 py-2 border transition-colors ${
              selectedMember
                ? "border-amber-600 text-amber-300 hover:bg-amber-900/30"
                : "border-zinc-800 text-zinc-600 cursor-not-allowed"
            }`}
          >
            Assign
          </button>
        </div>
      )}
    </div>
  );
}

function ConsumableItemCard({ item, members, onAssign, canManage }) {
  const [selectedMember, setSelectedMember] = useState("");
  const [quantity, setQuantity] = useState(1);

  const imageUrl = CONSUMABLE_IMAGES[item.consumable_type] || "/recovery-ether.png";
  const consumableName = item.consumable_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  return (
    <div className="bg-zinc-950 border border-amber-900/30 hover:border-amber-700/50 transition-colors overflow-hidden" data-testid={`consumable-item-${item.id}`}>
      <div className="bg-zinc-900/40 p-6 flex items-center justify-center" style={{ minHeight: '160px' }}>
        <img src={imageUrl} alt={consumableName} className="max-w-full max-h-[120px] object-contain" />
      </div>

      <div className="p-4 space-y-3">
        <div>
          <h4 className="font-cinzel text-amber-300 tracking-wide text-sm">{consumableName}</h4>
          <p className="text-xs text-zinc-500 mt-1 tabular-nums">Available: {item.quantity}</p>
        </div>
        {canManage && (
          <div className="space-y-2">
            <select
              value={selectedMember}
              onChange={e => setSelectedMember(e.target.value)}
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 text-sm"
              data-testid={`assign-consumable-select-${item.id}`}
            >
              <option value="">Select member...</option>
              {members?.map(m => (
                <option key={m.user_id} value={m.user_id}>{m.name}</option>
              ))}
            </select>
            {item.quantity > 1 && (
              <input
                type="number"
                min="1"
                max={item.quantity}
                value={quantity}
                onChange={e => setQuantity(Math.max(1, Math.min(item.quantity, parseInt(e.target.value) || 1)))}
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 text-sm tabular-nums"
                placeholder="Quantity"
              />
            )}
            <button
              onClick={() => onAssign(item.id, selectedMember, quantity)}
              disabled={!selectedMember}
              data-testid={`assign-consumable-btn-${item.id}`}
              className={`w-full text-[11px] uppercase tracking-widest px-2.5 py-2 border transition-colors ${
                selectedMember
                  ? "border-amber-600 text-amber-300 hover:bg-amber-900/30"
                  : "border-zinc-800 text-zinc-600 cursor-not-allowed"
              }`}
            >
              Assign
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function AssignedItemCard({ item, currentUserId, onReturn, busy }) {
  const memberName = item.assigned_to_name || 'Unknown';
  const isMyGear = item.assigned_to === currentUserId;

  if (item.item_type === 'gear') {
    const gear = item.gear_details || {};
    const rarity = gear.rarity || "common";
    const meta = RARITY[rarity] || RARITY.common;

    return (
      <div className={`border ${meta.border} ${meta.bg} p-4`}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className={`font-cinzel ${meta.text} tracking-wide`}>{gear.name || 'Unknown Gear'}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
              {gear.kind} {gear.class_req ? `· ${gear.class_req}` : '· any class'}
            </div>
          </div>
          <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border rounded-sm ${meta.text} ${meta.border} ${meta.bg}`}>
            {meta.label}
          </span>
        </div>
        <ItemMeta item={gear} />
        <div className="flex items-center justify-between mt-2">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">
            Assigned to: <span className={isMyGear ? "text-amber-300" : "text-violet-400"}>{isMyGear ? "You" : memberName}</span>
          </p>
          {isMyGear && (
            <button
              onClick={() => onReturn(item.id)}
              disabled={busy}
              data-testid={`return-gear-${item.id}`}
              className="text-[10px] uppercase tracking-widest px-2.5 py-1 border border-zinc-700 text-zinc-400 hover:border-amber-600 hover:text-amber-300 transition-colors disabled:opacity-40"
            >
              Return to Armory
            </button>
          )}
        </div>
      </div>
    );
  } else {
    const imageUrl = CONSUMABLE_IMAGES[item.consumable_type] || "/recovery-ether.png";
    const consumableName = item.consumable_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

    return (
      <div className="bg-zinc-950 border border-amber-900/30 overflow-hidden">
        <div className="bg-zinc-900/40 p-6 flex items-center justify-center" style={{ minHeight: '160px' }}>
          <img src={imageUrl} alt={consumableName} className="max-w-full max-h-[120px] object-contain" />
        </div>
        <div className="p-4">
          <h4 className="font-cinzel text-amber-300 tracking-wide text-sm">{consumableName}</h4>
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
            Assigned to: <span className="text-violet-400">{memberName}</span>
          </p>
          {item.quantity > 1 && (
            <p className="text-xs text-zinc-500 tabular-nums">Quantity: {item.quantity}</p>
          )}
        </div>
      </div>
    );
  }
}
